import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { NaiveDate } from "@tento-chrono";
import { ListenerErrors, type Thrown } from "@tento-core/listener-errors";
import type { CellEntry } from "@tento-lona/sheets";
import type { RowLocalId } from "@tento-lona/sheets";
import type { SdkRequestError } from "@tento-lona/sheets";
import type { CellTarget } from "./cell-target.ts";
import {
  cellReadFailure,
  ClientDisposedError,
  type ClientLifecycle,
  type SheetHandleDisposedError,
} from "../client/handle-errors.ts";
import type { DataFireChange, RowCellCache, SubscribeHandle } from "../sync/row-cell-cache.ts";

/**
 * Progressive read of one column of a single-cardinality row
 * (`row.single()?.cell(day)`): sync-aware callbacks over the row's store,
 * with the fetch it starts. Every fire delivers the column's
 * `SheetRow.DateCell<null>` — its value and date (no id: a single-cardinality
 * row's cell is addressed by its column), stamped with the row that
 * read it — so it feeds `CellTarget.cell` as a `row.cells(range)` entry does.
 * It is a subscription, not a promise — the one awaitable single-cell read is
 * `row.single()?.get(day)`, which rejects when the fetch fails. Release it
 * with `dispose` (or `using h = row.single()!.cell(day)`).
 *
 * - `onLoading()` — fires when no data is cached and a fetch is pending
 * - `onData(cell, change)` — fires whenever data is available (may
 *   re-fire on sync updates). `change` carries metadata about the write
 *   that produced this fire — most importantly its originating `actor`.
 *   Plugins use this to opt out of self-echo rebinds:
 *
 *       h.onData((cell, change) => {
 *         if (change.actor === myActor) return;
 *         bindWithData(cell?.value);
 *       });
 *
 *   Sync-driven updates have `change.actor === null` (no local actor was
 *   stamped on the cell's last write).
 * - `onDataFull(cell, change)` — fires only when sync is fully complete;
 *   registered after that, it fires at once with the last complete value.
 * - `onError(err)` — fires when the fetch the handle started failed (the
 *   row's request was refused, unanswered or malformed: an
 *   `SdkRequestError`), its sheet was disposed
 *   (`SheetHandle.DisposedError`), or — for a standalone row's handle — its
 *   client was disposed (`LonaSdk.Client.DisposedError`); registered after
 *   that, it fires at once. Without it a failed fetch leaves the handle on
 *   its placeholder, so a caller that must know registers it.
 *
 * Each `on…` holds one callback; registering again replaces it. A callback
 * that throws stops the handle (no further callbacks, the subscription
 * released) and its error is reported once to the row's `ListenerErrorSink`
 * — the client's `listenerErrorHandler` — never into the row's other
 * subscribers or the code that delivered the change.
 * Uses RowCellCache.subscribe() for progressive loading with sync state.
 *
 * A handle a row of a sheet hands out (`row.single()?.cell(day)`)
 * carries that sheet's lifecycle (`CellHandle.Owner`): asked for on a
 * disposed sheet, the verb throws `SheetHandle.DisposedError` at once; when
 * the sheet is disposed before the deferred subscription starts, it issues
 * no request and delivers the error to `onError`; and when the sheet is
 * disposed while the subscription is active, the subscription is released
 * and `onError` receives the error (no `onData` after it). The row's
 * `RowCellCache` is shared across sheets, so the check is this handle's, not
 * the row's. A standalone row's handle (`client.rows.open(...)` then
 * `row.single()?.cell(day)`) carries its client's lifecycle the same way: after
 * `client.dispose()` the verb throws, a subscription not yet started issues
 * no request, and an active one is released — each reporting
 * `LonaSdk.Client.DisposedError` — while the shared `RowCellCache` stays as it
 * is.
 */
export class CellHandle implements Disposable {
  #loadingCallback: Option<() => void> = null;
  #dataCallback:
    Option<(cell: Option<CellTarget.HeldDateCell<null>>, change: DataFireChange) => void> = null;
  #dataFullCallback:
    Option<(cell: Option<CellTarget.HeldDateCell<null>>, change: DataFireChange) => void> = null;
  #errorCallback: Option<(err: CellHandle.Failure) => void> = null;
  /** The last fully-synced fire, replayed to a late `onDataFull`. */
  #lastFull: Option<{ readonly cell: Option<CellTarget.HeldDateCell<null>>; readonly change: DataFireChange }> = null;
  /** The fetch failure, replayed to a late `onError`. */
  #failure: Option<{ readonly err: CellHandle.Failure }> = null;
  #disposed = false;
  #subscribeHandle: Option<{ dispose(): void }> = null;
  /** The lifecycle that handed this handle out — its sheet's, or a
   *  standalone row's client's; `null` for a handle nothing owns. */
  readonly #owner: Option<CellHandle.Owner>;
  /** Stops watching the owner's disposal; set while subscribed. */
  #unwatchOwner: Option<() => void> = null;

  readonly #rh: CellHandle.Source;
  readonly #row: RowLocalId;
  readonly #range: NaiveDate.Partial;

  /**
   * @param row the row whose cell this handle reads, by its interned id —
   *   what each delivered `SheetRow.DateCell` carries as its `row`.
   * @param range the cell's column.
   * @param rh the row's cell source.
   * @param owner the sheet (or a standalone row's client) that hands this
   *   handle out (`CellHandle.Owner`): checked when the deferred
   *   subscription starts, and watched while it is active. `null` when
   *   nothing owns it.
   */
  constructor(
    row: RowLocalId,
    range: NaiveDate.Partial,
    rh: CellHandle.Source,
    owner: Option<CellHandle.Owner>,
  ) {
    this.#row = row;
    this.#range = range;
    this.#rh = rh;
    this.#owner = owner;
    // Defer start so callers can chain onLoading/onData before fetch begins
    queueMicrotask(() => this.#startSubscription());
  }

  /** The row this handle reads a cell of. */
  get row(): RowLocalId {
    return this.#row;
  }

  /** Fires when no data is cached and a fetch is in progress. */
  onLoading(cb: () => void): this {
    this.#loadingCallback = cb;
    return this;
  }

  /** Fires whenever data is available (may re-fire on sync updates).
   *  The second arg carries the actor of the write that produced this
   *  fire — see class doc. */
  onData(
    cb: (cell: Option<CellTarget.HeldDateCell<null>>, change: DataFireChange) => void,
  ): this {
    this.#dataCallback = cb;
    return this;
  }

  /** Fires only when sync is fully complete — at once when it already
   *  completed. The second arg carries the actor of the write that
   *  produced this fire — see class doc. */
  onDataFull(
    cb: (cell: Option<CellTarget.HeldDateCell<null>>, change: DataFireChange) => void,
  ): this {
    this.#dataFullCallback = cb;
    const last = this.#lastFull;
    // Nothing after a failure (the owner's disposal included): the last
    // full fire predates it.
    if (last != null && !this.#disposed && this.#failure == null) cb(last.cell, last.change);
    return this;
  }

  /** Fires when the fetch this handle started failed, or its owner was
   *  disposed — at once when that already happened. `err` is the row
   *  request's `SdkRequestError` (a failure that is not one failed in the
   *  SDK's own code and is reported as the `get-cells` request's, outcome
   *  `"faulted"`), the sheet's
   *  `SheetHandle.DisposedError`, or a standalone row's client's
   *  `LonaSdk.Client.DisposedError`. */
  onError(cb: (err: CellHandle.Failure) => void): this {
    this.#errorCallback = cb;
    const failure = this.#failure;
    if (failure != null && !this.#disposed) cb(failure.err);
    return this;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────

  /** Stop the callbacks and release the row subscription. Idempotent. */
  dispose(): void {
    this.#disposed = true;
    this.#loadingCallback = null;
    this.#dataCallback = null;
    this.#dataFullCallback = null;
    this.#errorCallback = null;
    this.#subscribeHandle?.dispose();
    this.#subscribeHandle = null;
    this.#unwatchOwner?.();
    this.#unwatchOwner = null;
  }

  [Symbol.dispose](): void {
    this.dispose();
  }

  #startSubscription(): void {
    if (this.#disposed) return;
    // The owner (sheet, or a standalone row's client) may have been disposed
    // between construction and this microtask (or before a held row built
    // the handle): no request then, the refusal goes to `onError` (replayed
    // to a late registration).
    const owner = this.#owner;
    try {
      owner?.assertLive();
    } catch (err) {
      this.#fail(err);
      return;
    }

    // Watch the owner BEFORE subscribing: `subscribe` runs its initial
    // callbacks synchronously, and one may dispose the sheet. Disposed while
    // active: release the subscription, then report.
    let ownerDisposed = false;
    this.#unwatchOwner = owner?.watch((err) => {
      ownerDisposed = true;
      this.#unwatchOwner = null;
      this.#subscribeHandle?.dispose();
      this.#subscribeHandle = null;
      this.#fail(err);
    }) ?? null;
    const stopped = (): boolean => this.#disposed || ownerDisposed;
    let handle: SubscribeHandle;
    try {
      handle = this.#subscribe(stopped);
    } catch (error) {
      // `subscribe` itself threw (a caller's callback cannot reach here:
      // `#guarded` catches it): nothing was registered; stop and report.
      this.#abandon(error);
      return;
    }
    // An initial callback disposed this handle or its sheet before
    // `subscribe` returned the subscription: release it (this subscriber
    // only — the row's `RowCellCache` is shared) and start no fetch.
    if (stopped()) {
      handle.dispose();
      return;
    }
    this.#subscribeHandle = handle;
    handle.fetchChecked().catch((err: Thrown) => {
      if (this.#subscribeHandle !== handle) return;
      // The caller's `onError` may throw. Nothing awaits this chain, so the
      // error must not become an unhandled rejection: `#guarded` stops the
      // handle and reports it, as any callback's.
      this.#guarded(() => this.#fail(err));
    });
  }

  /**
   * Run `step`, which calls the caller's own callbacks. One that throws
   * stops this handle (`#abandon`) instead of escaping into whatever
   * delivered the step — the row's fan-out, the deferred start, the fetch's
   * failure chain — so the error is reported once, here, and the row's
   * other subscribers are not involved. `RowRangeCellHandle` does the same.
   */
  #guarded(step: () => void): void {
    try {
      step();
    } catch (error) {
      this.#abandon(error);
    }
  }

  /**
   * A caller's callback threw: the handle stops (no further callbacks, the
   * row subscription released, the owner no longer watched) and the error
   * is reported out of band, as thrown — it is the caller's own code, not a
   * read failure — through the row's `ListenerErrorSink` (the client's
   * `listenerErrorHandler`), exactly once.
   */
  #abandon(error: Thrown): void {
    this.dispose();
    ListenerErrors.report(this.#rh.listenerErrors, [error], "CellHandle");
  }

  /** The row subscription delivering to this handle's callbacks while
   *  `stopped` is false. */
  #subscribe(stopped: () => boolean): SubscribeHandle {
    return this.#rh.subscribe(this.#range, {
      onPlaceholderData: () => {
        if (stopped()) return;
        this.#guarded(() => this.#loadingCallback?.());
      },
      onDataWithStage: (cell, _syncState, change) => {
        if (stopped()) return;
        this.#guarded(() => this.#dataCallback?.(this.#dated(cell ?? null), change));
      },
      onDataFull: (cell, change) => {
        if (stopped()) return;
        const dated = this.#dated(cell ?? null);
        this.#lastFull = { cell: dated, change };
        this.#guarded(() => {
          this.#dataCallback?.(dated, change);
          // `onData` may dispose this handle or its sheet:
          // check again before the next user callback.
          if (stopped()) return;
          this.#dataFullCallback?.(dated, change);
        });
      },
    }, null);
  }

  /** The model tier's cell as this handle delivers it: stamped with its
   *  kind and the row that read it. */
  #dated(entry: Option<CellEntry>): Option<CellTarget.HeldDateCell<null>> {
    if (entry == null) return null;
    return {
      kind: "date",
      row: this.#row,
      date: entry.date,
      id: null,
      value: entry.value,
      style: entry.style,
    };
  }

  /** Record `err`, typed (replayed to a late `onError`), and report it. */
  #fail(err: unknown): void {
    if (this.#disposed || this.#failure != null) return;
    const failure = cellReadFailure(err);
    this.#failure = { err: failure };
    this.#errorCallback?.(failure);
  }
}

/** Types that go with `CellHandle`: `Source`, `Failure`, `Owner`. */
export namespace CellHandle {
  /**
   * What a cell handle reads from: a row handle's column subscription, and
   * the row's `ListenerErrorSink`, where a callback's error is reported. A
   * test hands a fake that implements just this.
   */
  export type Source = Pick<RowCellCache, "subscribe" | "listenerErrors">;

  /**
   * What a cell subscription's `onError` receives (`CellHandle`,
   * `RowRangeCellHandle`): the failed read's `SdkRequestError`, the owning
   * sheet's `SheetHandle.DisposedError`, or — for a standalone row's
   * subscription — its client's `LonaSdk.Client.DisposedError`.
   */
  export type Failure =
    | SdkRequestError
    | SheetHandleDisposedError
    | ClientDisposedError;

  /**
   * The lifecycle a `CellHandle` belongs to — what a sheet row's
   * `single()?.cell(day)` passes as the constructor's `owner` (the sheet),
   * and a standalone row's (its client). A handle nothing owns passes
   * `null`.
   */
  export interface Owner {
    /** Throws the owner's disposal error (`SheetHandle.DisposedError`,
     *  `LonaSdk.Client.DisposedError`) once it was disposed. */
    assertLive(): void;
    /**
     * Calls `onDisposed` once, with the owner's disposal error, when the
     * owner is disposed. Returns the unregister (`dispose` of the handle
     * calls it).
     */
    watch(onDisposed: (err: Error) => void): Unsubscribe;
  }

  /**
   * A client's lifecycle as the owner of a standalone row's cell handle
   * built by `verb`: checked when the deferred read starts, and watched
   * while it is active. `client.dispose()` releases the subscription and
   * reports `LonaSdk.Client.DisposedError`; the row's shared
   * `RowCellCache` is not disposed.
   */
  export function clientOwner(lifecycle: ClientLifecycle, verb: string): Owner {
    return {
      assertLive: () => lifecycle.assertLive(verb),
      watch: (onDisposed) => lifecycle.onDisposed(() => onDisposed(new ClientDisposedError(verb))),
    };
  }
}
